import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerBody, DrawerFooter } from '@/components/molecules/Drawer';
import { Button } from '@/components/atoms/Button';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { useToast } from '@/context/ToastContext';
import styles from './PushReminderDrawer.module.scss';

const STORAGE_KEY = 'push-reminder-dismissed';
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const NOTIFICATIONS_ENABLED = import.meta.env.VITE_ENABLE_NOTIFICATIONS === 'true';

function getShouldShow(dismissedAt: number | null): boolean {
  if (!dismissedAt) return true;
  return Date.now() - dismissedAt > COOLDOWN_MS;
}

interface PushReminderDrawerProps {
  /**
   * Si es true, el drawer no se abrirá y se cerrará si ya estaba abierto. Pensado
   * para evitar solapamientos con otros prompts globales del primer acceso
   * (p. ej. el WelcomeOnboardingDialog del estudiante).
   */
  suppress?: boolean;
}

export function PushReminderDrawer({ suppress = false }: PushReminderDrawerProps = {}) {
  const { t } = useTranslation();
  const { permission, isSubscribed, isLoading, subscribe } = usePushNotifications();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!isNaN(parsed)) setDismissedAt(parsed);
      }
    } catch {
      // ignore
    }
  }, []);

  // Si nos suprimen mientras está abierto, cerramos sin penalizar (no
  // marcamos dismissedAt para no consumir el cooldown sin intención del usuario).
  useEffect(() => {
    if (suppress && open) setOpen(false);
  }, [suppress, open]);

  // Show drawer when: notifications enabled, push supported, not subscribed, not dismissed (or cooldown passed)
  useEffect(() => {
    if (suppress) return;
    if (!NOTIFICATIONS_ENABLED) return;
    if (permission === 'unsupported') return;
    if (isSubscribed) return;
    if (!getShouldShow(dismissedAt)) return;

    // Small delay so it doesn't pop immediately on load
    const openTimer = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(openTimer);
  {/* eslint-disable-next-line react-hooks/exhaustive-deps */}
  }, [NOTIFICATIONS_ENABLED, permission, isSubscribed, dismissedAt, suppress]);

  const handleDismiss = () => {
    setOpen(false);
    const now = Date.now();
    setDismissedAt(now);
    try {
      localStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      // ignore
    }
  };

  const handleEnable = async () => {
    const ok = await subscribe();
    if (ok) {
      handleDismiss();
    } else {
      toast({ description: t('settings.notifications.pushEnableError') });
    }
  };

  if (!NOTIFICATIONS_ENABLED || permission === 'unsupported') return null;

  return (
    <Drawer open={open} onOpenChange={(o) => !o && handleDismiss()}>
      <DrawerContent
          side="bottom"
          onSwipeToClose={handleDismiss}
          onEscapeKeyDown={handleDismiss}
          onPointerDownOutside={handleDismiss}
          closeSrLabel={t('header.close')}
        >
        <DrawerHeader>
          <div className={styles.pushReminder__icon}>
            <Bell size={28} />
          </div>
          <DrawerTitle>{t('pushReminder.title')}</DrawerTitle>
          <DrawerDescription>
            {permission === 'denied'
              ? t('pushReminder.descriptionDenied')
              : t('pushReminder.descriptionDefault')}
          </DrawerDescription>
        </DrawerHeader>
        <DrawerBody>
          {permission === 'denied' && (
            <p className={styles.pushReminder__hint}>
              {t('pushReminder.hintDenied')}
            </p>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={handleDismiss}>
            {t('pushReminder.notNow')}
          </Button>
          {permission !== 'denied' && (
            <Button
              variant="primary"
              onClick={handleEnable}
              isLoading={isLoading}
              leftIcon={<Bell size={16} />}
            >
              {t('pushReminder.enable')}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
